/**
 * QR Code Registry - Defines all supported QR types with their fields and payload builders
 */

/** Types with a single main input - show hero input at top */
export const SIMPLE_TYPES = ['url', 'text', 'email', 'phone', 'sms', 'social', 'appstore', 'file', 'crypto'];

export const MAIN_PLACEHOLDERS = {
  url: 'Paste your link here...',
  text: 'Enter your text...',
  email: 'Enter email address...',
  phone: 'Enter phone number...',
  sms: 'Enter phone number...',
  social: 'Paste profile URL...',
  appstore: 'Paste app store URL...',
  file: 'Paste file URL...',
  crypto: 'Enter wallet address...',
};

export const QR_TYPES = {
  url: {
    id: 'url',
    label: 'URL',
    icon: '🔗',
    category: 'basic',
    primaryFieldId: 'url',
    fields: [
      { id: 'url', label: 'Website URL', type: 'url', placeholder: 'https://example.com', required: true },
    ],
    buildPayload: (data) => {
      let url = data.url?.trim() || '';
      if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
      return url;
    },
  },
  text: {
    id: 'text',
    label: 'Text',
    icon: '📝',
    category: 'basic',
    primaryFieldId: 'text',
    fields: [
      { id: 'text', label: 'Plain text', type: 'textarea', placeholder: 'Enter any text...', required: true },
    ],
    buildPayload: (data) => data.text?.trim() || '',
  },
  email: {
    id: 'email',
    label: 'Email',
    icon: '✉️',
    category: 'basic',
    primaryFieldId: 'email',
    fields: [
      { id: 'email', label: 'Email address', type: 'email', placeholder: 'hello@example.com', required: true },
      { id: 'subject', label: 'Subject (optional)', type: 'text', placeholder: 'Email subject' },
      { id: 'body', label: 'Body (optional)', type: 'textarea', placeholder: 'Email body...' },
    ],
    buildPayload: (data) => {
      const email = data.email?.trim() || '';
      if (!email) return '';
      let mailto = `mailto:${encodeURIComponent(email)}`;
      const params = [];
      if (data.subject?.trim()) params.push(`subject=${encodeURIComponent(data.subject.trim())}`);
      if (data.body?.trim()) params.push(`body=${encodeURIComponent(data.body.trim())}`);
      if (params.length) mailto += '?' + params.join('&');
      return mailto;
    },
  },
  phone: {
    id: 'phone',
    label: 'Phone',
    icon: '📞',
    category: 'basic',
    primaryFieldId: 'phone',
    fields: [
      { id: 'phone', label: 'Phone number', type: 'tel', placeholder: '+1 234 567 8900', required: true },
    ],
    buildPayload: (data) => {
      const phone = (data.phone || '').replace(/\D/g, '');
      return phone ? `tel:${phone}` : '';
    },
  },
  sms: {
    id: 'sms',
    label: 'SMS',
    icon: '💬',
    category: 'basic',
    primaryFieldId: 'phone',
    fields: [
      { id: 'phone', label: 'Phone number', type: 'tel', placeholder: '+1 234 567 8900', required: true },
      { id: 'message', label: 'Message (optional)', type: 'textarea', placeholder: 'Pre-filled SMS text' },
    ],
    buildPayload: (data) => {
      const phone = (data.phone || '').replace(/\D/g, '');
      if (!phone) return '';
      let sms = `sms:${phone}`;
      if (data.message?.trim()) sms += `?body=${encodeURIComponent(data.message.trim())}`;
      return sms;
    },
  },
  wifi: {
    id: 'wifi',
    label: 'WiFi',
    icon: '📶',
    category: 'advanced',
    fields: [
      { id: 'ssid', label: 'Network name (SSID)', type: 'text', placeholder: 'MyWiFi', required: true },
      { id: 'password', label: 'Password', type: 'password', placeholder: 'Network password' },
      { id: 'encryption', label: 'Encryption', type: 'select', options: ['WPA', 'WEP', 'nopass'], default: 'WPA' },
      { id: 'hidden', label: 'Hidden network', type: 'checkbox', default: false },
    ],
    buildPayload: (data) => {
      const ssid = data.ssid?.trim() || '';
      if (!ssid) return '';
      const enc = data.encryption || 'WPA';
      const pwd = enc === 'nopass' ? '' : (data.password || '');
      const hidden = data.hidden ? 'H:true' : 'H:false';
      return `WIFI:T:${enc};S:${ssid};P:${pwd};${hidden};;`;
    },
  },
  vcard: {
    id: 'vcard',
    label: 'vCard',
    icon: '👤',
    category: 'advanced',
    fields: [
      { id: 'firstName', label: 'First name', type: 'text', placeholder: 'John', required: true },
      { id: 'lastName', label: 'Last name', type: 'text', placeholder: 'Doe', required: true },
      { id: 'organization', label: 'Organization', type: 'text', placeholder: 'Company' },
      { id: 'title', label: 'Title', type: 'text', placeholder: 'Developer' },
      { id: 'phone', label: 'Phone', type: 'tel', placeholder: '+1 234 567 8900' },
      { id: 'email', label: 'Email', type: 'email', placeholder: 'john@example.com' },
      { id: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com' },
      { id: 'address', label: 'Address', type: 'text', placeholder: '123 Main St, City' },
    ],
    buildPayload: (data) => {
      const escape = (s) => (s || '').replace(/[;,\\]/g, '\\$&');
      const fn = escape(data.firstName?.trim() || '');
      const ln = escape(data.lastName?.trim() || '');
      if (!fn && !ln) return '';
      const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `N:${ln};${fn};;;`,
        `FN:${fn} ${ln}`.trim(),
      ];
      if (data.organization?.trim()) lines.push(`ORG:${escape(data.organization.trim())}`);
      if (data.title?.trim()) lines.push(`TITLE:${escape(data.title.trim())}`);
      if (data.phone?.trim()) lines.push(`TEL:${(data.phone || '').replace(/\D/g, '')}`);
      if (data.email?.trim()) lines.push(`EMAIL:${escape(data.email.trim())}`);
      if (data.website?.trim()) lines.push(`URL:${data.website.trim()}`);
      if (data.address?.trim()) lines.push(`ADR:;;${escape(data.address.trim())};;;;`);
      lines.push('END:VCARD');
      return lines.join('\r\n');
    },
  },
  location: {
    id: 'location',
    label: 'Location',
    icon: '📍',
    category: 'advanced',
    fields: [
      { id: 'lat', label: 'Latitude', type: 'number', placeholder: '40.7128', required: true },
      { id: 'lng', label: 'Longitude', type: 'number', placeholder: '-74.0060', required: true },
      { id: 'query', label: 'Or search place', type: 'text', placeholder: 'Times Square, NYC' },
    ],
    buildPayload: (data) => {
      const lat = parseFloat(data.lat);
      const lng = parseFloat(data.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        return `geo:${lat},${lng}`;
      }
      const q = data.query?.trim();
      if (q) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
      return '';
    },
  },
  event: {
    id: 'event',
    label: 'Event',
    icon: '📅',
    category: 'advanced',
    fields: [
      { id: 'title', label: 'Event title', type: 'text', placeholder: 'Meeting', required: true },
      { id: 'start', label: 'Start (YYYYMMDDTHHMMSS)', type: 'text', placeholder: '20251225T100000' },
      { id: 'end', label: 'End (YYYYMMDDTHHMMSS)', type: 'text', placeholder: '20251225T110000' },
      { id: 'location', label: 'Location', type: 'text', placeholder: 'Conference Room' },
      { id: 'description', label: 'Description', type: 'textarea', placeholder: 'Event details...' },
    ],
    buildPayload: (data) => {
      const title = data.title?.trim() || '';
      if (!title) return '';
      const start = data.start?.trim() || '20251225T100000';
      const end = data.end?.trim() || start;
      const loc = data.location?.trim() || '';
      const desc = data.description?.trim() || '';
      const lines = [
        'BEGIN:VEVENT',
        `SUMMARY:${title.replace(/[,;\\]/g, '\\$&')}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        'END:VEVENT',
      ];
      if (loc) lines.splice(4, 0, `LOCATION:${loc.replace(/[,;\\]/g, '\\$&')}`);
      if (desc) lines.splice(5, 0, `DESCRIPTION:${desc.replace(/[,;\\]/g, '\\$&')}`);
      return 'BEGIN:VCALENDAR\r\nVERSION:2.0\r\n' + lines.join('\r\n') + '\r\nEND:VCALENDAR';
    },
  },
  social: {
    id: 'social',
    label: 'Social',
    icon: '📱',
    category: 'extended',
    primaryFieldId: 'url',
    fields: [
      { id: 'platform', label: 'Platform', type: 'select', options: ['Instagram', 'Twitter/X', 'LinkedIn', 'Facebook', 'TikTok', 'YouTube', 'Custom'], default: 'Instagram' },
      { id: 'url', label: 'Profile URL', type: 'url', placeholder: 'https://instagram.com/username', required: true },
    ],
    buildPayload: (data) => {
      let url = data.url?.trim() || '';
      if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
      return url;
    },
  },
  appstore: {
    id: 'appstore',
    label: 'App',
    icon: '📲',
    category: 'extended',
    primaryFieldId: 'url',
    fields: [
      { id: 'platform', label: 'Store', type: 'select', options: ['iOS', 'Android'], default: 'iOS' },
      { id: 'url', label: 'App URL', type: 'url', placeholder: 'https://apps.apple.com/app/...', required: true },
    ],
    buildPayload: (data) => {
      let url = data.url?.trim() || '';
      if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
      return url;
    },
  },
  crypto: {
    id: 'crypto',
    label: 'Crypto',
    icon: '₿',
    category: 'extended',
    primaryFieldId: 'address',
    fields: [
      { id: 'currency', label: 'Currency', type: 'select', options: ['BTC', 'ETH', 'SOL', 'Custom'], default: 'BTC' },
      { id: 'address', label: 'Wallet address', type: 'text', placeholder: 'bc1q... or 0x...', required: true },
      { id: 'amount', label: 'Amount (optional)', type: 'text', placeholder: '0.001' },
      { id: 'message', label: 'Message (optional)', type: 'text', placeholder: 'Payment for...' },
    ],
    buildPayload: (data) => {
      const addr = data.address?.trim() || '';
      if (!addr) return '';
      const curr = (data.currency || 'BTC').toUpperCase();
      if (curr === 'BTC') {
        let btc = `bitcoin:${addr}`;
        const params = [];
        if (data.amount?.trim()) params.push(`amount=${data.amount.trim()}`);
        if (data.message?.trim()) params.push(`message=${encodeURIComponent(data.message.trim())}`);
        if (params.length) btc += '?' + params.join('&');
        return btc;
      }
      if (curr === 'ETH') {
        let eth = `ethereum:${addr}`;
        if (data.amount?.trim()) eth += `@${data.amount.trim()}`;
        if (data.message?.trim()) eth += `?message=${encodeURIComponent(data.message.trim())}`;
        return eth;
      }
      return addr;
    },
  },
  file: {
    id: 'file',
    label: 'File',
    icon: '📄',
    category: 'extended',
    primaryFieldId: 'url',
    fields: [
      { id: 'url', label: 'File URL (PDF, Image, etc.)', type: 'url', placeholder: 'https://example.com/file.pdf', required: true },
      { id: 'type', label: 'Type', type: 'select', options: ['PDF', 'Image', 'Audio', 'Other'], default: 'PDF' },
    ],
    buildPayload: (data) => {
      let url = data.url?.trim() || '';
      if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
      return url;
    },
  },
};

export const QR_CATEGORIES = [
  { id: 'basic', label: 'Basic' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'extended', label: 'Extended' },
];

export const getQrType = (id) => QR_TYPES[id] || QR_TYPES.url;
export const getQrTypesByCategory = (category) =>
  Object.values(QR_TYPES).filter((t) => t.category === category);
